/**
 * "Who wants the Business School Festival" — demand by festival.
 *
 * The other half of the demand report. It answers a different question from the
 * city table beside it, and **it is not a rollup of that table**: one
 * institution wanting three cities is three city rows, so a festival figure
 * summed from them would count it three times. Every number here is the
 * server's own.
 *
 * ── WHY THIS PANEL IS NOT OPTIONAL ──────────────────────────────────────────
 *
 * 🔴 **THREE OF THE SEVEN FESTIVALS TOUR ONE PLACE AND ARE ASKED NEITHER A
 * SELECTION NOR CITIES** (contract §5), so they have **no city rows at all**.
 * A festival vocabulary — or a festival answer — derived from the city table
 * would be silently missing exactly those three, and missing them in the way
 * that looks like no interest rather than like no cities.
 *
 * ⚠️ **This is also the master the submissions filter reads its festivals
 * from**, for that reason: a filter offers what can MATCH, and a list built
 * from the city table could never offer a single-location festival.
 *
 * ── WHAT THE COLUMNS SAY ────────────────────────────────────────────────────
 *
 * ⚠️ **`Said no` IS A COLUMN, and dropping it would lose a fact.** A row exists
 * for a festival answered `no` as well as `yes` (contract §3), because "said
 * no" and "was never asked" are different things and the second is the ABSENCE
 * of a row. A table showing only the yeses cannot tell a festival nobody wants
 * from one nobody was offered.
 *
 * ⚠️ **`Whole tour` is here as well as on the city table, deliberately.** It is
 * what explains the city figures: a festival with a high whole-tour count has
 * city numbers that are mostly not explicit picks, which is the composition the
 * city table states in words.
 */

import {
  Button,
  EmptyState,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EventInterestIcon } from './icons';
import { DEMAND_LABELS, UNAPPROVED_SPLIT_NOTE } from './event-interest-wording';

/**
 * One festival, with the answers recorded against it.
 *
 * ⚠️ **A VIEW MODEL, so every wire field name is spelled once in the page's
 * adapter** — see `DemandCityRow` for the reasoning. It matters most on the
 * four count fields here, which are structurally identical and which nothing
 * would flag if two were swapped.
 */
export interface DemandFestivalRow {
  key: string;
  festivalId: number;
  festivalName: string;
  /** Institutions that said yes to this festival. */
  yes: number;
  /** Institutions that said no. **A real answer, not an absence.** */
  no: number;
  /** Of the yeses, how many took the travel package. */
  travelPackage: number;
  /**
   * Of the yeses, how many asked for the whole tour rather than naming cities.
   *
   * ⚠️ **These are the institutions that store NO city rows**, and therefore
   * the ones a city figure built from explicit picks alone would miss.
   */
  wholeTour: number;
  /**
   * Of {@link yes}, how many nobody has approved.
   *
   * 🔴 **A SUBSET OF THE YESES — never of `yes + no`, and never a population to
   * add back on.** It counts within the interested half alone.
   */
  notApproved: number;
}

interface FestivalColumn {
  key: string;
  label: string;
  width: number;
  numeric: boolean;
  value: (row: DemandFestivalRow) => number | string;
  muted?: boolean;
}

/**
 * ✅ **ONE ARRAY DRIVES THE HEADER, THE BODY, THE SKELETON AND THE FLOOR** — the
 * four-parallel-lists failure `/invoices` records costs a column shift as the
 * rows arrive, and neither `tsc` nor a test can see it.
 */
const FESTIVAL_COLUMNS: readonly FestivalColumn[] = [
  {
    key: 'festival',
    label: DEMAND_LABELS.festival,
    width: 260,
    numeric: false,
    value: (row) => row.festivalName,
  },
  { key: 'yes', label: 'Said yes', width: 110, numeric: true, value: (row) => row.yes },
  {
    key: 'no',
    label: 'Said no',
    width: 110,
    numeric: true,
    muted: true,
    value: (row) => row.no,
  },
  {
    key: 'travelPackage',
    label: 'Travel package',
    width: 150,
    numeric: true,
    muted: true,
    value: (row) => row.travelPackage,
  },
  {
    key: 'wholeTour',
    label: DEMAND_LABELS.wholeTour,
    width: 130,
    numeric: true,
    muted: true,
    value: (row) => row.wholeTour,
  },
  /*
   * ⚠️ **THE LAST THREE COLUMNS ARE ALL SUBSETS OF `Said yes`, WHICH IS WHY
   * THEY SIT TOGETHER AFTER THE YES/NO PAIR.** Travel package, whole tour and
   * not approved each count within the interested half; none of them is a
   * third answer alongside `Said no`, and none may be added to the row.
   */
  {
    key: 'notApproved',
    label: DEMAND_LABELS.notApproved,
    width: 150,
    numeric: true,
    muted: true,
    value: (row) => row.notApproved,
  },
];

const TABLE_MIN_WIDTH = FESTIVAL_COLUMNS.reduce((total, column) => total + column.width, 0);
const SKELETON_ROWS = 4;

export interface FestivalDemandPanelProps {
  rows: readonly DemandFestivalRow[];
  loading: boolean;
  error: string | null;
  /** Narrows the submissions list to this festival. */
  onOpenSubmissions: (festivalId: number) => void;
}

export function FestivalDemandPanel({
  rows,
  loading,
  error,
  onOpenSubmissions,
}: FestivalDemandPanelProps) {
  return (
    <Panel>
      <PanelHeader title="Demand by festival" />

      {error && (
        <div className="border-b border-border px-4 py-3">
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load demand.</>}>
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        </div>
      )}

      {/*
        ⛔ **NOT HELPER TEXT — a fact about the data the reader cannot otherwise
        see, which is the documented exception.** Nothing in these figures
        distinguishes an institution somebody has approved from one nobody has
        looked at, and the split is what stops a single number being quoted.
        A sweep must not delete it. See `event-interest-wording.ts`.
      */}
      <p
        className="m-0 border-b border-border px-4 py-2.5 text-xs text-fg-muted [overflow-wrap:anywhere]"
        title={UNAPPROVED_SPLIT_NOTE.detail}
      >
        {UNAPPROVED_SPLIT_NOTE.short}
      </p>

      {!loading && rows.length === 0 ? (
        <EmptyState
          icon={<EventInterestIcon />}
          title={error === null ? 'No festival has been answered yet' : 'Nothing to show'}
          description={
            error === null
              ? 'Festivals appear here as institutions answer the interest form.'
              : 'Demand couldn’t be loaded. Check the message above and try again.'
          }
        />
      ) : (
        <>
          {/* ── Below `md`: a card per festival ─────────────────────────────
              Seven columns will not fit 320px, and every figure is on the
              card: one that exists only above a breakpoint is
              indistinguishable from one that was removed. */}
          <div className="md:hidden">
            {loading ? (
              <ul className="m-0 list-none p-0">
                {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                  <li key={index} className="border-b border-border px-4 py-3 last:border-b-0">
                    <div className="flex flex-col gap-2">
                      <Skeleton className="h-3 w-44" />
                      <Skeleton className="h-2.5 w-32" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="m-0 list-none p-0">
                {rows.map((row) => (
                  <li key={row.key} className="border-b border-border px-4 py-3 last:border-b-0">
                    <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                      {row.festivalName}
                    </p>
                    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      {FESTIVAL_COLUMNS.filter((column) => column.numeric).map((column) => (
                        <div key={column.key} className="contents">
                          <dt className="m-0 text-fg-muted">{column.label}</dt>
                          <dd className="m-0 text-right text-fg-secondary tabular">
                            {column.value(row)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <div className="mt-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onOpenSubmissions(row.festivalId)}
                      >
                        See who
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── From `md`: the table ──────────────────────────────────────── */}
          <div className="hidden md:block">
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              {/* The floor goes through `style`: Tailwind v4 scans source text,
                  so an interpolated `min-w-[…]` emits no rule at all. */}
              <Table style={{ minWidth: TABLE_MIN_WIDTH + 140 }}>
                <THead>
                  <TR>
                    {FESTIVAL_COLUMNS.map((column) => (
                      <TH key={column.key} numeric={column.numeric}>
                        {column.label}
                      </TH>
                    ))}
                    <TH>
                      <span className="sr-only">Event interest submissions</span>
                    </TH>
                  </TR>
                </THead>
                {loading ? (
                  <TBody>
                    {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                      <TR key={index}>
                        {FESTIVAL_COLUMNS.map((column) => (
                          <TD key={column.key} numeric={column.numeric}>
                            <Skeleton className={column.numeric ? 'ml-auto h-3 w-8' : 'h-3 w-40'} />
                          </TD>
                        ))}
                        <TD>
                          <Skeleton className="h-3 w-16" />
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                ) : (
                  <TBody>
                    {rows.map((row) => (
                      <TR key={row.key}>
                        {FESTIVAL_COLUMNS.map((column) => (
                          <TD key={column.key} numeric={column.numeric}>
                            <span
                              className={
                                column.numeric
                                  ? column.muted
                                    ? 'tabular text-sm text-fg-muted'
                                    : 'tabular text-sm font-medium text-fg'
                                  : 'text-sm text-fg'
                              }
                            >
                              {column.value(row)}
                            </span>
                          </TD>
                        ))}
                        <TD>
                          {/*
                            The drill-down, and the reason the festival table
                            earns a control the city table does not: narrowing
                            the submissions list by FESTIVAL is exact, where
                            narrowing it by CITY would silently drop every
                            whole-tour taker — the institutions that store no
                            city row. See the page's own note on why there is
                            no city filter.
                          */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onOpenSubmissions(row.festivalId)}
                          >
                            See who
                          </Button>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                )}
              </Table>
            </TableWrap>
          </div>
        </>
      )}
    </Panel>
  );
}
